import { AfterContentInit, Component, ContentChild, ElementRef, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { Subscription, fromEvent, asyncScheduler } from 'rxjs';
import { FilterFieldDirective, FilterFieldMode } from '../../directives/filter-field.directive';

export const FILTER_MODE_DESCRIPTIONS = {
  'contains' : "Filter value found inside row field value (case insensitive substring)",
  '=' : "Exact match required (case insensitive)",
  '!=' : "NOT equal",
  '*' : "Wildcard (case insensitive)",
  '!*' : "NOT Wildcard",
  '>=' : "Text sort value is >= filter (useful for sorting on timestamps)",
  '<' : "Text sort value is < filter (useful for sorting on timestamps)",
  '~*': "Regular expression matching (ex. '^a.*z$' will match anything starting with 'a' and ending with 'z')",
  'empty': "Value is null or evaluates to an empty string",
  'not empty': "Value is null and does not evaluate to an empty string"
};

export interface FilterableField {
  elementRef: any;
  mode: FilterFieldMode;
  setMode( mode: FilterFieldMode): void;
  getNativeInputElement(): HTMLInputElement | null;
  filterEditFn?: (newValSetter?:(newVal:string)=>void) => Promise<boolean>;
  modalEditFn?: () => Promise<void>;
}

@Component({ template: "" })
export class FilterFieldConfigBase implements OnDestroy, AfterContentInit {

  filterField: FilterableField;

  @ViewChild('menuButton') set menuButtonSet(menuButtonRef: ElementRef) {
    this.menuButtonRef = menuButtonRef
    this.btnBlurSubscription?.unsubscribe()
    if ( menuButtonRef ) {
      this.btnBlurSubscription = fromEvent(menuButtonRef.nativeElement, 'blur').subscribe( event => {
        this.onLostFocus()
      })
    }
  }

  @Output('modeSelected') modeSelected$ = new EventEmitter<FilterFieldMode>()

  menuButtonRef: ElementRef
  bShowButton = false

  titles = FILTER_MODE_DESCRIPTIONS;

  modes : FilterFieldMode[] = [ 'contains', '=', '!=', '*', '!*', '>=', '<', '~*', 'empty', 'not empty']

  modeNeedsInput = true
  focusSubscription: Subscription
  blurSubscription: Subscription
  btnBlurSubscription: Subscription

  constructor(public elementRef: ElementRef) {
  }

  alwaysShowButton = () => this.filterField.filterEditFn || this.filterField.mode != 'contains';

  ngAfterContentInit() {
    if ( this.filterField ) {
      const nativeInput = this.filterField.getNativeInputElement();
      this.modeNeedsInput = this.isTextInputMode(this.filterField.mode)
      this.focusSubscription = fromEvent(nativeInput, 'focus').subscribe( event => {
        this.bShowButton = true
      })
      this.blurSubscription = fromEvent(nativeInput, 'blur').subscribe( event => {
        this.onLostFocus()
      })
      if ( this.alwaysShowButton() ) {
        this.bShowButton = true
      }
    } else {
      console.warn('No filterField component present!');
    }
  }


  onLostFocus() {
    const bHasFocus = this.elementRef?.nativeElement.matches(':focus-within');
    this.bShowButton = bHasFocus || this.alwaysShowButton();
  }

  ngOnDestroy() {
    this.focusSubscription?.unsubscribe()
    this.blurSubscription?.unsubscribe()
    this.btnBlurSubscription?.unsubscribe()
  }

  isTextInputMode = (mode: FilterFieldMode) => !['empty','not empty'].includes(mode);

  modeSelected(mode:FilterFieldMode) {
    this.modeNeedsInput = this.isTextInputMode(mode);
    this.filterField.getNativeInputElement().focus()
    this.handleModeChange(mode);
    this.modeSelected$.emit(mode);
  }

  handleModeChange(mode:FilterFieldMode){
    this.onLostFocus();
  }

}

@Component({
  selector: 'app-filter-field-config',
  templateUrl: './filter-field-config.component.html',
  styleUrls: ['./filter-field-config.component.css']
})
export class FilterFieldConfigComponent extends FilterFieldConfigBase {

  @ContentChild(FilterFieldDirective) set filterFieldSet(filterFieldImpl: FilterableField){
    this.filterField = filterFieldImpl;
  }

  handleModeChange(mode: FilterFieldMode): void {
    const filterDirective = this.filterField as any;
    filterDirective.setMode(mode);
    filterDirective.filterList();
  }
}
